import { gatewayApi } from '@/lib/hrApi';

// Performance Management — talks to performance-service via the gateway
// (/api/v1/performance/*). gatewayApi attaches the access token + handles refresh.
const BASE = '/api/v1/performance';

export type GoalStatus = 'not_started' | 'on_track' | 'at_risk' | 'completed' | 'paused' | 'archived';
export type MeasurementType = 'percentage' | 'number' | 'currency' | 'boolean' | 'milestones' | 'rating' | 'formula' | 'manual';
export interface ConnectedWork {
  provider: string; remote_type: string; remote_id: string;
  remote_name?: string; remote_url?: string;
  progress_mode: 'evidence' | 'calculated';
  sync_status: string; last_synced_at?: string | null;
  tasks_total: number; tasks_done: number;
  milestones_total: number; milestones_done: number; overdue_count: number;
  external_status: 'open' | 'deleted' | 'archived';
}
export type GoalLevel = 'company' | 'department' | 'individual';
export interface Goal {
  id: string; employee_id: string; cycle_id?: string | null;
  title: string; description?: string; weight: number; progress: number;
  status: GoalStatus; due_date?: string | null; created_at: string; updated_at: string;
  measurement_type: MeasurementType;
  target?: number | null; actual?: number | null; stretch_target?: number | null;
  scoring_cap?: number | null; display_max?: number | null; weight_locked_at?: string | null;
  goal_level: GoalLevel; parent_goal_id?: string | null;
  category_id?: string | null; org_unit_id?: string | null;
  // derived by the service — the explainable overachievement view
  raw_attainment: number; score_contribution: number; display_ceiling: number;
  stretch_achieved?: boolean; // actual >= stretch_target (numeric goals); informational marker
  progress_source: 'manual' | 'calculated' | 'connected';
  connected_work?: ConnectedWork | null;
}
export interface AlignmentLink { id: string; title: string; goal_level: GoalLevel; progress: number; status: string }
export interface GoalWeightSummary {
  employee_id: string; cycle_id?: string | null; total_weight: number; valid: boolean;
  goals: { id: string; title: string; weight: number }[];
}
export interface GoalVersion {
  id: string; field: string; previous_value: string; new_value: string;
  changed_by?: string | null; reason?: string; approved: boolean; created_at: string;
}
export interface Cycle {
  id: string; name: string; type: string; start_date: string; end_date: string;
  self_review_due_date?: string | null; manager_review_due_date?: string | null;
  status: 'draft' | 'active' | 'in_review' | 'closed' | 'cancelled';
  comp_linked?: boolean;
  double_blind?: boolean;
  recurrence?: 'none' | 'monthly' | 'quarterly' | 'half_yearly' | 'annual';
  assessment_style?: 'written' | 'goal_based';
  rate_competencies?: boolean;
  framework_id?: string | null;
  framework_snapshot?: any;
}
export interface Achievement {
  id: string; employee_id: string; title: string; description?: string;
  impact?: string; tags: string[]; entry_date: string; source: string; created_at: string;
}
export interface ReviewResponse { id: string; author: 'self' | 'manager'; section: string; question?: string; answer: string; rating?: number | null; }
export interface Review {
  id: string; cycle_id: string; employee_id: string; manager_id?: string | null;
  status: 'pending' | 'self_in_progress' | 'manager_in_progress' | 'finalized';
  self_review_status: 'not_started' | 'in_progress' | 'submitted';
  manager_review_status: 'not_started' | 'in_progress' | 'submitted';
  final_rating?: number | null; submitted_at?: string | null; finalized_at?: string | null;
  released_at?: string | null; acknowledged_at?: string | null; self_hidden?: boolean;
  updated_at: string; responses?: ReviewResponse[];
  // US-10 — one canonical status per review, derived server-side by a documented
  // precedence (acknowledged > released > finalized > manager_submitted >
  // self_submitted > self_in_progress > pending). Prefer this over the raw
  // self_/manager_ states so the UI never shows contradictory chips at once.
  derived_status?: string;
}

const data = <T,>(p: Promise<{ data: T }>) => p.then((r) => r.data);

// --- Cycles ---
export const listCycles = () => data<Cycle[]>(gatewayApi.get(`${BASE}/cycles`));
export const createCycle = (b: Partial<Cycle>) => data<Cycle>(gatewayApi.post(`${BASE}/cycles`, b));
export const updateCycle = (id: string, b: Partial<Cycle>) => data<Cycle>(gatewayApi.put(`${BASE}/cycles/${id}`, b));
export const deleteCycle = (id: string) => gatewayApi.delete(`${BASE}/cycles/${id}`).then((r) => r.data);
export const nextCycle = (id: string) => data<Cycle>(gatewayApi.post(`${BASE}/cycles/${id}/next`, {}));

// --- Goals ---
export const listGoals = (employeeId?: string, level?: GoalLevel) =>
  data<Goal[]>(gatewayApi.get(`${BASE}/goals`, { params: { ...(employeeId ? { employee_id: employeeId } : {}), ...(level ? { level } : {}) } }));
export const getAlignment = (goalId: string) =>
  data<AlignmentLink[]>(gatewayApi.get(`${BASE}/goals/${goalId}/alignment`));
// Connected work (goal ↔ Asana project) — authored through migration-service.
export interface AsanaGoalProject { gid: string; name: string; permalink_url?: string }
export const listGoalProjects = () =>
  data<{ projects: AsanaGoalProject[] }>(gatewayApi.get(`/api/v1/integrations/asana/goal-projects`)).then((r) => r.projects ?? []);
export const getGoalLink = (goalId: string) =>
  data<{ goal_id: string; project_gid: string; remote_url?: string }>(gatewayApi.get(`/api/v1/integrations/asana/goal-links/${goalId}`));
export const linkGoal = (b: { goal_id: string; project_gid: string; mode: 'evidence' | 'calculated' }) =>
  data<{ status: string }>(gatewayApi.post(`/api/v1/integrations/asana/goal-links`, b));
export const unlinkGoal = (goalId: string) =>
  data<{ status: string }>(gatewayApi.delete(`/api/v1/integrations/asana/goal-links/${goalId}`));
export interface GoalInput {
  title?: string; description?: string; weight?: number; due_date?: string; cycle_id?: string;
  measurement_type?: MeasurementType; target?: number; actual?: number; stretch_target?: number;
  scoring_cap?: number; display_max?: number; change_reason?: string; progress?: number; status?: GoalStatus;
  goal_level?: GoalLevel; parent_goal_id?: string; clear_parent?: boolean;
  category_id?: string; org_unit_id?: string;
}
// US-15 — a manager/HR may assign a goal to a named employee. The backend
// `create` handler already honours ?employee_id=<uuid> for callers who pass
// canActOn (falling back to the caller otherwise), so ownership is authorized
// server-side; we just forward the picked assignee.
export const createGoal = (b: GoalInput, employeeId?: string) =>
  data<Goal>(gatewayApi.post(`${BASE}/goals`, b, employeeId ? { params: { employee_id: employeeId } } : undefined));
export const updateGoal = (id: string, b: GoalInput) => data<Goal>(gatewayApi.put(`${BASE}/goals/${id}`, b));
export const updateGoalProgress = (id: string, b: { progress?: number; actual?: number; status?: GoalStatus }) =>
  data<Goal>(gatewayApi.patch(`${BASE}/goals/${id}/progress`, b));
export const archiveGoal = (id: string) => gatewayApi.delete(`${BASE}/goals/${id}`).then((r) => r.data);
export const getGoalWeights = (p?: { cycle_id?: string; employee_id?: string }) =>
  data<GoalWeightSummary>(gatewayApi.get(`${BASE}/goals/weights`, { params: p }));
export const getGoalVersions = (id: string) => data<GoalVersion[]>(gatewayApi.get(`${BASE}/goals/${id}/versions`));

// --- Achievements (journal) ---
export const listAchievements = () => data<Achievement[]>(gatewayApi.get(`${BASE}/journal`));
export const createAchievement = (b: { title: string; description?: string; impact?: string; tags?: string[] }) =>
  data<Achievement>(gatewayApi.post(`${BASE}/journal`, b));
export const deleteAchievement = (id: string) => gatewayApi.delete(`${BASE}/journal/${id}`).then((r) => r.data);

// --- Reviews ---
export const listMyReviews = () => data<Review[]>(gatewayApi.get(`${BASE}/reviews`));
export const listTeamReviews = () => data<Review[]>(gatewayApi.get(`${BASE}/reviews/team`));
export const getReview = (id: string) => data<Review>(gatewayApi.get(`${BASE}/reviews/${id}`));
export const submitSelfReview = (b: { cycle_id: string; submit: boolean; responses: Array<{ section: string; answer: string; rating?: number }> }) =>
  data<Review>(gatewayApi.post(`${BASE}/reviews/self`, b));
export const submitManagerReview = (b: { review_id: string; submit: boolean; final_rating?: number; responses: Array<{ section: string; answer: string; rating?: number }> }) =>
  data<Review>(gatewayApi.post(`${BASE}/reviews/manager`, b));
export const startReview = (b: { cycle_id: string; employee_id: string; manager_id?: string }) =>
  data<Review>(gatewayApi.post(`${BASE}/reviews/start`, b));
export const finalizeReview = (b: { review_id: string; final_rating?: number }) =>
  data<Review>(gatewayApi.post(`${BASE}/reviews/finalize`, b));
export const releaseReview = (id: string) => data<Review>(gatewayApi.post(`${BASE}/reviews/${id}/release`, {}));
/** Manager self-release: finalize + release a manager's own report in one step
 * (only when the workspace has enabled manager_self_release). */
export const managerReleaseReview = (id: string) => data<Review>(gatewayApi.post(`${BASE}/reviews/${id}/manager-release`, {}));
export const acknowledgeReview = (id: string) => data<Review>(gatewayApi.post(`${BASE}/reviews/${id}/acknowledge`, {}));

// Download the finalized review as a PDF (streamed by performance-service from the
// frozen snapshot). Server enforces authz + availability (finalized; released for
// the employee). Triggers a browser download; returns nothing.
export const downloadReviewPDF = async (id: string, filename = 'performance-review.pdf', company?: string) => {
  const resp = await gatewayApi.get(`${BASE}/reviews/${id}/review.pdf`, {
    responseType: 'blob',
    params: company ? { company } : undefined,
  });
  const url = URL.createObjectURL(resp.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// --- Per-tenant performance settings ---
export interface PerfSettings { manager_self_release: boolean; }
export const getPerfSettings = () => data<PerfSettings>(gatewayApi.get(`${BASE}/reviews/settings`));
export const updatePerfSettings = (b: PerfSettings) => data<PerfSettings>(gatewayApi.put(`${BASE}/reviews/settings`, b));

// --- Scoring transparency (rating scales as data + weighted rollup) ---
export interface RatingOption { value: number; label: string; ordinal: number; }
export interface RatingScale { id?: string; name: string; version?: number; is_default: boolean; options: RatingOption[]; }
export interface FormulaComponent { key: string; label: string; weight: number; }
export interface ScoringFormula {
  id?: string; name: string; version?: number; rounding_dp: number;
  missing_handling: 'redistribute' | 'zero' | 'exclude'; components: FormulaComponent[]; is_default: boolean;
}
export interface ScoredComponent {
  key: string; label: string; raw_score: number | null; weight: number; weighted: number; present: boolean;
}
export interface ReviewScore {
  review_id?: string; formula_version_id?: string | null; overall: number; total_weight: number; components: ScoredComponent[];
}
// scores: component key -> rating-scale value (null/omitted = not scored)
export type ScoreInput = { scores: Record<string, number | null> };

export const getRatingScales = () => data<RatingScale[]>(gatewayApi.get(`${BASE}/rating-scales`));
export const createRatingScale = (b: Partial<RatingScale>) => data<RatingScale>(gatewayApi.post(`${BASE}/rating-scales`, b));
export const getScoringFormula = () => data<ScoringFormula>(gatewayApi.get(`${BASE}/scoring-formula`));
export const saveScoringFormula = (b: Partial<ScoringFormula>) => data<ScoringFormula>(gatewayApi.put(`${BASE}/scoring-formula`, b));

// --- Review templates (section/question sets) ---
export interface Template {
  id: string; name: string; description?: string; sections: string[] | any; is_default: boolean;
  version?: number; status?: string; created_at?: string;
}
export interface TemplateInput { name?: string; description?: string; sections?: string[] | any; is_default?: boolean; }
export const listTemplates = () => data<Template[]>(gatewayApi.get(`${BASE}/templates`));
export const createTemplate = (b: TemplateInput) => data<Template>(gatewayApi.post(`${BASE}/templates`, b));
export const updateTemplate = (id: string, b: TemplateInput) => data<Template>(gatewayApi.put(`${BASE}/templates/${id}`, b));
export const deleteTemplate = (id: string) => gatewayApi.delete(`${BASE}/templates/${id}`).then((r) => r.data);
export const previewReviewScore = (reviewId: string, b: ScoreInput) =>
  data<ReviewScore>(gatewayApi.post(`${BASE}/reviews/${reviewId}/score/preview`, b));
export const saveReviewScore = (reviewId: string, b: ScoreInput) =>
  data<ReviewScore>(gatewayApi.post(`${BASE}/reviews/${reviewId}/score`, b));
export const getReviewScore = (reviewId: string) => data<ReviewScore>(gatewayApi.get(`${BASE}/reviews/${reviewId}/score`));

// --- Goal-based assessment (per-goal ratings) ---
// Under a goal_based cycle the self and manager rate each goal on the rating
// scale; the manager ratings roll up (weight-weighted) into the goals component
// score server-side. author 'self' is the reviewee's own rating; 'manager' is
// the manager-of-record's / HR's.
export interface GoalRating { goal_id: string; author: 'self' | 'manager'; rating: number; comment?: string; updated_at: string; }
export const listGoalRatings = (reviewId: string) =>
  data<GoalRating[]>(gatewayApi.get(`${BASE}/reviews/${reviewId}/goal-ratings`));
export const saveGoalRating = (reviewId: string, goalId: string, b: { author: 'self' | 'manager'; rating: number; comment?: string }) =>
  data<GoalRating>(gatewayApi.put(`${BASE}/reviews/${reviewId}/goals/${goalId}/rating`, b));

// --- Competency model (library, cycle assignment, per-review ratings) ---
export interface Competency {
  id: string; tenant_id: string; name: string; description?: string; category?: string;
  active: boolean; source: 'native' | 'workforce_import'; source_ref?: string;
  created_at: string; updated_at: string;
}
export interface CycleCompetency { competency_id: string; name: string; weight: number; }
export interface CompetencyRating { competency_id: string; author: 'self' | 'manager'; rating: number; comment?: string; updated_at: string; }

export const listCompetencies = (activeOnly = false) =>
  data<Competency[]>(gatewayApi.get(`${BASE}/competencies`, { params: activeOnly ? { active: 'true' } : {} }));
export const createCompetency = (b: { name: string; description?: string; category?: string }) =>
  data<Competency>(gatewayApi.post(`${BASE}/competencies`, b));
export const updateCompetency = (id: string, b: { name?: string; description?: string; category?: string; active?: boolean }) =>
  data<Competency>(gatewayApi.put(`${BASE}/competencies/${id}`, b));
export const deleteCompetency = (id: string) => gatewayApi.delete(`${BASE}/competencies/${id}`).then((r) => r.data);
export const getCycleCompetencies = (cycleId: string) =>
  data<CycleCompetency[]>(gatewayApi.get(`${BASE}/cycles/${cycleId}/competencies`));
export const setCycleCompetencies = (cycleId: string, rows: { competency_id: string; weight: number }[]) =>
  data<CycleCompetency[]>(gatewayApi.put(`${BASE}/cycles/${cycleId}/competencies`, rows));
export const listCompetencyRatings = (reviewId: string) =>
  data<CompetencyRating[]>(gatewayApi.get(`${BASE}/reviews/${reviewId}/competency-ratings`));
export const saveCompetencyRating = (reviewId: string, competencyId: string, b: { author: 'self' | 'manager'; rating: number; comment?: string }) =>
  data<CompetencyRating>(gatewayApi.put(`${BASE}/reviews/${reviewId}/competencies/${competencyId}/rating`, b));
// Import competency definitions (names only) from a workforce framework (C5).
export const importCompetencies = (rows: { key: string; name: string; description?: string }[]) =>
  data<{ imported: number; skipped: number }>(gatewayApi.post(`${BASE}/competencies/import`, { competencies: rows }));

// --- Review-cycle population (eligibility preview + bulk launch) ---
export interface PopulationFilters {
  departments?: string[];
  employment_types?: string[];
  manager_ids?: string[];
  include_employees?: string[];
  exclude_employees?: string[];
  min_tenure_days?: number;
  /** Multi-entity G7: only people EMPLOYED by these companies (primary or a
   *  second employment), reviewed by that employment's manager. */
  legal_entities?: string[];
}
export interface EligibleMember {
  employee_id: string; user_id?: string | null; manager_user_id?: string | null;
  full_name: string; department_id?: string | null; department_name?: string;
  position?: string; employment_type?: string; reason?: string;
}
export interface PopulationResult {
  included: EligibleMember[]; excluded: EligibleMember[]; included_count: number; excluded_count: number;
}
export interface LaunchSummary {
  cycle_id: string; participant_count: number; reviews_created: number; excluded_count: number;
}
export const previewPopulation = (cycleId: string, f: PopulationFilters) =>
  data<PopulationResult>(gatewayApi.post(`${BASE}/cycles/${cycleId}/population/preview`, f));
export const launchCycle = (cycleId: string, f: PopulationFilters) =>
  data<LaunchSummary>(gatewayApi.post(`${BASE}/cycles/${cycleId}/launch`, f));
export const getCycleParticipants = (cycleId: string) =>
  data<EligibleMember[]>(gatewayApi.get(`${BASE}/cycles/${cycleId}/participants`));

// --- Continuous performance: check-ins (progress timeline) + milestones ---
export interface ProgressUpdate {
  id: string; goal_id: string; progress?: number | null; actual?: number | null;
  note?: string; evidence_url?: string; created_by?: string | null; created_at: string;
}
export interface Milestone {
  id: string; goal_id: string; title: string; due_date?: string | null;
  done: boolean; done_at?: string | null; ordinal: number; created_at: string;
}
export interface CheckInInput {
  progress?: number; actual?: number; status?: GoalStatus; note?: string; evidence_url?: string;
}
export interface MilestoneInput { title?: string; due_date?: string; done?: boolean; ordinal?: number; }

export const getGoalUpdates = (goalId: string) => data<ProgressUpdate[]>(gatewayApi.get(`${BASE}/goals/${goalId}/updates`));
export const addGoalUpdate = (goalId: string, b: CheckInInput) => data<Goal>(gatewayApi.post(`${BASE}/goals/${goalId}/updates`, b));
export const getMilestones = (goalId: string) => data<Milestone[]>(gatewayApi.get(`${BASE}/goals/${goalId}/milestones`));
export const addMilestone = (goalId: string, b: MilestoneInput) => data<Milestone>(gatewayApi.post(`${BASE}/goals/${goalId}/milestones`, b));
export const updateMilestone = (goalId: string, mid: string, b: MilestoneInput) => data<Milestone>(gatewayApi.patch(`${BASE}/goals/${goalId}/milestones/${mid}`, b));
export const deleteMilestone = (goalId: string, mid: string) => gatewayApi.delete(`${BASE}/goals/${goalId}/milestones/${mid}`).then((r) => r.data);

// --- Peer / 360 feedback ---
export interface FeedbackPerson { user_id: string; name: string; position?: string; department?: string; }
export interface FeedbackRequest {
  id: string; cycle_id?: string | null; subject_user_id: string; subject_name?: string;
  provider_user_id: string; provider_name?: string; message?: string; anonymous: boolean; status: string; created_at: string;
}
export interface FeedbackRequestInput {
  subject_user_id?: string; cycle_id?: string; message?: string; anonymous?: boolean; provider_user_ids: string[];
}
export interface FeedbackResponseInput { strengths?: string; improvements?: string; rating?: number; }
export interface FeedbackItem {
  provider_name?: string; anonymous: boolean; strengths?: string; improvements?: string; rating?: number | null; created_at: string;
}
export interface FeedbackAboutMe {
  total_requested: number; submitted_count: number; threshold: number; anon_pending_reveal: number; items: FeedbackItem[];
}
export const getFeedbackPeople = () => data<FeedbackPerson[]>(gatewayApi.get(`${BASE}/feedback/people`));
export const requestFeedback = (b: FeedbackRequestInput) => data<{ created: number }>(gatewayApi.post(`${BASE}/feedback/requests`, b));
export const getFeedbackInbox = () => data<FeedbackRequest[]>(gatewayApi.get(`${BASE}/feedback/requests`));
export const respondFeedback = (id: string, b: FeedbackResponseInput) => data<{ status: string }>(gatewayApi.post(`${BASE}/feedback/requests/${id}/respond`, b));
export const declineFeedback = (id: string) => data<{ status: string }>(gatewayApi.post(`${BASE}/feedback/requests/${id}/decline`, {}));
export const getFeedbackAboutMe = () => data<FeedbackAboutMe>(gatewayApi.get(`${BASE}/feedback/about-me`));

// --- Reports (preview-before-download) ---
export interface ReportSummary {
  participants: number; self_submitted: number; manager_submitted: number; finalized: number;
  completion_pct: number; avg_final_score?: number | null; avg_final_rating?: number | null;
}
export interface RatingBucket { bucket: number; count: number; }
export interface ReportRow {
  employee_id: string; name: string; status: string; self_status: string; manager_status: string;
  final_rating?: number | null; final_score?: number | null;
}
export interface CycleReport {
  cycle_id: string; cycle_name: string; status: string;
  summary: ReportSummary; rating_distribution: RatingBucket[]; rows: ReportRow[];
}
export const getCycleReport = (cycleId: string) => data<CycleReport>(gatewayApi.get(`${BASE}/reports/cycle/${cycleId}`));

export interface CycleTrend {
  cycle_id: string; cycle_name: string; status: string; participants: number; completion_pct: number; avg_final_rating?: number | null;
}
export interface DeptBreakdown {
  department: string; participants: number; finalized: number; completion_pct: number; avg_rating?: number | null;
}
export const getPerformanceOverview = () => data<CycleTrend[]>(gatewayApi.get(`${BASE}/reports/overview`));
export const getCycleDepartments = (cycleId: string) => data<DeptBreakdown[]>(gatewayApi.get(`${BASE}/reports/cycle/${cycleId}/departments`));

// --- Calibration ---
export interface CalibrationRow {
  review_id: string; subject_user_id: string; name: string; status: string;
  final_rating?: number | null; final_score?: number | null; calibrated_rating?: number | null;
}
export interface CalibrationBoard {
  session_id: string; cycle_id: string; cycle_name: string; status: string;
  rows: CalibrationRow[]; distribution: RatingBucket[];
}
export interface CalibrationChange {
  id: string; review_id: string; subject_user_id: string; field: string;
  previous_value?: number | null; new_value?: number | null; reason: string; changed_by?: string | null; created_at: string;
}
export interface AdjustInput { rating?: number; score?: number; reason: string; }
export const getCalibrationBoard = (cycleId: string) => data<CalibrationBoard>(gatewayApi.get(`${BASE}/calibration/cycle/${cycleId}`));
export const getCalibrationChanges = (cycleId: string) => data<CalibrationChange[]>(gatewayApi.get(`${BASE}/calibration/cycle/${cycleId}/changes`));
export const closeCalibration = (cycleId: string) => data<{ status: string }>(gatewayApi.post(`${BASE}/calibration/cycle/${cycleId}/close`, {}));
export const adjustCalibration = (reviewId: string, b: AdjustInput) => data<CalibrationRow>(gatewayApi.post(`${BASE}/calibration/reviews/${reviewId}/adjust`, b));

// --- Compensation (recommend → approve; payroll handoff gated per cycle) ---
export interface CompRecommendation {
  id: string; cycle_id?: string | null; subject_user_id: string; subject_name?: string; review_id?: string | null;
  kind: 'bonus' | 'merit' | 'recognition'; amount?: number | null; currency: string; percent?: number | null; period?: string;
  rationale?: string; status: 'recommended' | 'approved' | 'rejected';
  paid_at?: string | null; payroll_run_id?: string | null; reversed_at?: string | null; reverse_reason?: string;
  pool_id?: string | null; computed_amount?: number | null;
  recommended_by?: string | null; approved_by?: string | null; decision_note?: string; pushed_to_payroll: boolean;
  created_at: string; decided_at?: string | null;
}
export interface CompRecommendInput {
  cycle_id?: string; subject_user_id: string; review_id?: string; kind: 'bonus' | 'merit' | 'recognition';
  amount?: number; currency?: string; percent?: number; period?: string; rationale?: string;
}
export const listCompensation = (cycleId: string) => data<CompRecommendation[]>(gatewayApi.get(`${BASE}/compensation/cycle/${cycleId}`));
export const recommendCompensation = (b: CompRecommendInput) => data<CompRecommendation>(gatewayApi.post(`${BASE}/compensation/recommendations`, b));
export const approveCompensation = (id: string, note?: string) => data<CompRecommendation>(gatewayApi.post(`${BASE}/compensation/recommendations/${id}/approve`, { note }));
export const rejectCompensation = (id: string, note?: string) => data<CompRecommendation>(gatewayApi.post(`${BASE}/compensation/recommendations/${id}/reject`, { note }));
export const setCompLinked = (cycleId: string, linked: boolean) => data<{ comp_linked: boolean }>(gatewayApi.post(`${BASE}/compensation/cycle/${cycleId}/comp-linked`, { linked }));
export interface CompCandidate {
  user_id: string; employee_id: string; name: string; department?: string;
  final_rating?: number | null; final_score?: number | null; review_id?: string | null;
}
export const getCompCandidates = (cycleId: string) => data<CompCandidate[]>(gatewayApi.get(`${BASE}/compensation/cycle/${cycleId}/candidates`));
export const getCompHistory = (subjectUserId: string) => data<CompRecommendation[]>(gatewayApi.get(`${BASE}/compensation/subject/${subjectUserId}/history`));

// Goal-linked bonus engine: a bonus pot allocated across goals by weight; each goal
// earns achievement% × its allocation (overachievement pays more). Total = the
// performance-linked bonus; base + bonus = total CTC.
export interface BonusGoalLine { goal_id: string; title: string; weight: number; achievement: number; payout: number; targeted: number; earned: number; }
export interface BonusPolicy {
  id: string; tenant_id?: string | null; name: string; version: number; status: string;
  effective_from?: string | null; effective_to?: string | null;
  department_id?: string | null; employment_type?: string | null; career_level?: string | null;
  method: 'percent_of_salary' | 'fixed_amount' | 'band_table' | 'manual_only';
  percent?: number | null; amount?: number | null; currency: string;
  band_table?: any; salary_basis: string; payout_curve?: any;
}
export const listBonusPolicies = () => data<BonusPolicy[]>(gatewayApi.get(`${BASE}/bonus-policies`));
export const createBonusPolicy = (b: any) => data<BonusPolicy>(gatewayApi.post(`${BASE}/bonus-policies`, b));
export const activateBonusPolicy = (id: string) => data<BonusPolicy>(gatewayApi.post(`${BASE}/bonus-policies/${id}/activate`, {}));
export const retireBonusPolicy = (id: string) => data<BonusPolicy>(gatewayApi.post(`${BASE}/bonus-policies/${id}/retire`, {}));
export interface ResolveTargetsSummary {
  cycle_id: string; resolved: number; skipped: number;
  rows: Array<{ subject_user_id: string; name: string; outcome: string; policy_name?: string; eligible_salary?: string; target_amount?: string; currency?: string }>;
}
export const resolveBonusTargets = (cycleId: string) => data<ResolveTargetsSummary>(gatewayApi.post(`${BASE}/compensation/cycle/${cycleId}/resolve-targets`, {}));
export interface BonusBreakdown {
  cycle_id: string; subject_user_id: string; currency: string;
  annual_bonus_target: number; quarterly_bonus: number; cycle_pot: number;
  target_source?: 'manual' | 'policy'; policy_name?: string; eligible_salary?: number;
  goals: BonusGoalLine[]; total_bonus: number;
}
export const getBonusBreakdown = (cycleId: string, userId: string) => data<BonusBreakdown>(gatewayApi.get(`${BASE}/compensation/cycle/${cycleId}/bonus/${userId}`));
export const saveBonusPlan = (cycleId: string, userId: string, b: { annual_bonus_target: number; currency?: string }) => data<BonusBreakdown>(gatewayApi.put(`${BASE}/compensation/cycle/${cycleId}/bonus/${userId}`, b));

export interface BonusReportRow { name: string; title: string; manager_name: string; target_bonus: number; earned_bonus: number; percent_achieved: number; }
export interface BonusReport {
  cycle_name: string; currency: string; rows: BonusReportRow[];
  total_target: number; total_earned: number; total_percent: number;
}
export const getBonusReport = (cycleId: string) => data<BonusReport>(gatewayApi.get(`${BASE}/compensation/cycle/${cycleId}/bonus-report`));

// --- Development plans + PIPs (governance) ---
// Track P (Performance × Learning): actions carry a type, a lifecycle status
// (evidenced = learning evidence attached, waiting for a human to close) and an
// optional learning_ref. `text`/`done` stay required so the legacy PUT-plan
// reconcile ({text, done} + id) keeps working; everything else is server-set.
export type DevActionType = 'manual' | 'training' | 'goal';
export type DevActionStatus = 'open' | 'evidenced' | 'done';
export type DevLearningRefKind = 'assignment' | 'program' | 'session' | 'course';
export interface DevLearningRef { kind: DevLearningRefKind; id: string; }
export interface DevAction {
  id?: string; plan_id?: string; sequence?: number; type?: DevActionType;
  text: string; done: boolean; status?: DevActionStatus;
  target_date?: string | null; done_at?: string | null; done_by?: string | null;
  learning_ref?: DevLearningRef | null; competency_ids?: string[];
}
export interface DevPlan {
  id: string; subject_user_id: string; cycle_id?: string | null; title: string; description?: string;
  actions: DevAction[]; status: 'active' | 'completed' | 'archived'; created_by?: string | null; created_at: string; updated_at: string;
}
export interface DevPlanInput {
  subject_user_id?: string; cycle_id?: string; title?: string; description?: string; actions?: DevAction[]; status?: string;
}
/** Action-level endpoints (preferred over PUT-plan for single-action edits). */
export interface DevActionInput {
  type?: DevActionType; title: string; target_date?: string; learning_ref?: DevLearningRef; competency_ids?: string[];
}
export interface DevActionPatch {
  title?: string; type?: DevActionType; target_date?: string; status?: 'open' | 'done';
  learning_ref?: DevLearningRef | null; competency_ids?: string[];
}
/** Exactly one of course_id / session_id. */
export interface AssignTrainingInput { course_id?: string; session_id?: string; due_date?: string; note?: string; }
export type DevEvidenceSourceType =
  | 'training_completion' | 'training_verified' | 'practice_completed' | 'certification_issued' | 'readiness_ready';
export interface DevEvidence {
  id: string; subject_user_id: string; source: 'learning'; source_type: DevEvidenceSourceType; source_id: string;
  title: string; occurred_at: string; verified: boolean; program_id?: string | null; session_id?: string | null;
  competency_ids: string[]; snapshot?: Record<string, unknown> | null;
  action_id?: string | null; plan_id?: string | null; action_title?: string | null; created_at: string;
}
export interface PIP {
  id: string; subject_user_id: string; manager_user_id?: string | null; reason?: string; objectives?: string;
  start_date?: string | null; end_date?: string | null; status: 'open' | 'succeeded' | 'failed' | 'closed';
  outcome_note?: string; created_at: string; updated_at: string;
}
export interface PIPInput {
  subject_user_id?: string; manager_user_id?: string; reason?: string; objectives?: string;
  start_date?: string; end_date?: string; status?: string; outcome_note?: string;
}
export const listDevPlans = () => data<DevPlan[]>(gatewayApi.get(`${BASE}/development-plans`));
export const createDevPlan = (b: DevPlanInput) => data<DevPlan>(gatewayApi.post(`${BASE}/development-plans`, b));
export const updateDevPlan = (id: string, b: DevPlanInput) => data<DevPlan>(gatewayApi.put(`${BASE}/development-plans/${id}`, b));
export const deleteDevPlan = (id: string) => gatewayApi.delete(`${BASE}/development-plans/${id}`).then((r) => r.data);
export const addDevAction = (planId: string, b: DevActionInput) =>
  data<DevAction>(gatewayApi.post(`${BASE}/development-plans/${planId}/actions`, b));
export const updateDevAction = (planId: string, actionId: string, b: DevActionPatch) =>
  data<DevAction>(gatewayApi.patch(`${BASE}/development-plans/${planId}/actions/${actionId}`, b));
export const deleteDevAction = (planId: string, actionId: string) =>
  gatewayApi.delete(`${BASE}/development-plans/${planId}/actions/${actionId}`).then((r) => r.data);
export const assignDevActionTraining = (planId: string, actionId: string, b: AssignTrainingInput) =>
  data<DevAction>(gatewayApi.post(`${BASE}/development-plans/${planId}/actions/${actionId}/assign-training`, b));
export const listDevEvidence = (params?: { subject_user_id?: string; plan_id?: string }) =>
  data<DevEvidence[]>(gatewayApi.get(`${BASE}/development-evidence`, { params: params && Object.keys(params).length ? params : undefined }));
export const listPIPs = (subjectUserId?: string) =>
  data<PIP[]>(gatewayApi.get(`${BASE}/pips`, { params: subjectUserId ? { subject_user_id: subjectUserId } : undefined }));
export const createPIP = (b: PIPInput) => data<PIP>(gatewayApi.post(`${BASE}/pips`, b));
export const updatePIP = (id: string, b: PIPInput) => data<PIP>(gatewayApi.put(`${BASE}/pips/${id}`, b));

// --- 1:1 meetings ---
export interface OneOnOneItem {
  id: string; kind: 'agenda' | 'action'; text: string; owner_user_id?: string | null;
  done: boolean; due_date?: string | null; ordinal: number; created_at: string;
}
export interface OneOnOne {
  id: string; owner_user_id: string; with_user_id: string; owner_name?: string; with_name?: string;
  counterpart_name?: string; title?: string; scheduled_at?: string | null; cadence: string; status: string;
  shared_notes?: string; created_at: string; items?: OneOnOneItem[]; my_private_note?: string;
}
export interface OneOnOneInput { with_user_id: string; title?: string; scheduled_at?: string; cadence?: string;   meeting_timezone?: string;
}
export interface OneOnOneItemInput { kind?: 'agenda' | 'action'; text?: string; done?: boolean; due_date?: string; }
export const listOneOnOnes = () => data<OneOnOne[]>(gatewayApi.get(`${BASE}/one-on-ones`));
export const getOneOnOne = (id: string) => data<OneOnOne>(gatewayApi.get(`${BASE}/one-on-ones/${id}`));
export const createOneOnOne = (b: OneOnOneInput) => data<OneOnOne>(gatewayApi.post(`${BASE}/one-on-ones`, b));
export const updateOneOnOne = (id: string, b: { scheduled_at?: string; status?: string; shared_notes?: string }) => data<OneOnOne>(gatewayApi.patch(`${BASE}/one-on-ones/${id}`, b));
export const addOneOnOneItem = (id: string, b: OneOnOneItemInput) => data<OneOnOneItem>(gatewayApi.post(`${BASE}/one-on-ones/${id}/items`, b));
export const updateOneOnOneItem = (id: string, itemId: string, b: OneOnOneItemInput) => data<OneOnOneItem>(gatewayApi.patch(`${BASE}/one-on-ones/${id}/items/${itemId}`, b));
export const deleteOneOnOneItem = (id: string, itemId: string) => gatewayApi.delete(`${BASE}/one-on-ones/${id}/items/${itemId}`).then((r) => r.data);
export const saveOneOnOnePrivateNote = (id: string, note: string) => data<{ status: string }>(gatewayApi.put(`${BASE}/one-on-ones/${id}/private-note`, { note }));
export const getOneOnOneOpenActions = (withUserId: string) => data<OneOnOneItem[]>(gatewayApi.get(`${BASE}/one-on-ones/open-actions`, { params: { with_user_id: withUserId } }));

// --- Phase 1: goal categories + framework bundles ---------------------------
export interface GoalCategory {
  id: string; tenant_id?: string | null; key: string; label: string; description?: string;
  counts_toward_score: boolean; bonus_eligible: boolean; formula_component_key?: string;
  ordinal: number; active: boolean;
}
export const listGoalCategories = () => data<GoalCategory[]>(gatewayApi.get(`${BASE}/goal-categories`));
export const createGoalCategory = (b: Partial<GoalCategory>) => data<GoalCategory>(gatewayApi.post(`${BASE}/goal-categories`, b));
export const updateGoalCategory = (id: string, b: Partial<GoalCategory>) => data<GoalCategory>(gatewayApi.put(`${BASE}/goal-categories/${id}`, b));
export const deleteGoalCategory = (id: string) => gatewayApi.delete(`${BASE}/goal-categories/${id}`).then((r) => r.data);

// --- Recognition Values (tenant-configurable "What for?" catalogue) ----------
// Product term "Recognition Value" == the recognition_category storage. Admin
// CRUD is gated by recognition:configure server-side.
export const MAX_ACTIVE_RECOGNITION_VALUES = 12;
export interface RecognitionValue {
  id: string; key: string; label: string; description?: string;
  active: boolean; sort_order: number; archived: boolean; archived_at?: string | null;
  in_use: boolean; created_at: string; updated_at: string;
}
const REC = `${BASE}/recognition/values`;
export const listRecognitionValues = () => data<RecognitionValue[]>(gatewayApi.get(REC));
export const createRecognitionValue = (b: { label: string; description?: string }) =>
  data<RecognitionValue>(gatewayApi.post(REC, b));
export const updateRecognitionValue = (id: string, b: { label?: string; description?: string; active?: boolean }) =>
  data<RecognitionValue>(gatewayApi.patch(`${REC}/${id}`, b));
export const reorderRecognitionValues = (ids: string[]) =>
  data<{ ok: boolean }>(gatewayApi.post(`${REC}/reorder`, { ids }));
export const archiveRecognitionValue = (id: string) => data<RecognitionValue>(gatewayApi.post(`${REC}/${id}/archive`));
export const restoreRecognitionValue = (id: string) => data<RecognitionValue>(gatewayApi.post(`${REC}/${id}/restore`));
export const deleteRecognitionValue = (id: string) => gatewayApi.delete(`${REC}/${id}`).then((r) => r.data);

export interface PerfFramework {
  id: string; tenant_id?: string | null; name: string; description?: string; status: string;
  scale_id?: string | null; formula_id?: string | null; template_id?: string | null;
  bonus_linked: boolean; weight_policy: { require_sum_100?: boolean; enforce_at?: string } | any;
  review_defaults: { assessment_style?: string; double_blind?: boolean; rate_competencies?: boolean } | any;
}
export const listFrameworks = () => data<PerfFramework[]>(gatewayApi.get(`${BASE}/frameworks`));
export const createFramework = (b: Partial<PerfFramework>) => data<PerfFramework>(gatewayApi.post(`${BASE}/frameworks`, b));
export const updateFramework = (id: string, b: Partial<PerfFramework>) => data<PerfFramework>(gatewayApi.put(`${BASE}/frameworks/${id}`, b));

// --- Phase 3: pools, reversal, merit proposals -------------------------------
export interface BonusPool {
  id: string; cycle_id: string; name: string; scope: 'company' | 'department'; scope_ref_id?: string | null;
  funding_amount: number; currency: string; allocation_method: string; status: string;
  proposed_total: number; approved_total: number;
}
export const listBonusPools = (cycleId: string) => data<BonusPool[]>(gatewayApi.get(`${BASE}/compensation/cycle/${cycleId}/pools`));
export const createBonusPool = (cycleId: string, b: Partial<BonusPool>) => data<BonusPool>(gatewayApi.post(`${BASE}/compensation/cycle/${cycleId}/pools`, b));
export const proposeFromPool = (poolId: string) => data<{ candidates: number; proposed: number; computed_total: string; scale_factor: string }>(gatewayApi.post(`${BASE}/compensation/pools/${poolId}/propose`, {}));
export const reverseAward = (id: string, reason: string) => data<CompRecommendation>(gatewayApi.post(`${BASE}/compensation/recommendations/${id}/reverse`, { reason }));
export interface MeritProposal {
  id: string; employee_id: string; percent?: number | null; amount?: number | null; currency?: string | null;
  effective_from: string; rationale?: string | null; status: string; current_base?: string; proposed_base?: string; created_at: string;
}
export const listMeritProposals = () => data<{ items: MeritProposal[] }>(gatewayApi.get(`/api/v1/payroll/compensation-proposals`)).then((r) => r.items || []);
export const decideMeritProposal = (id: string, confirm: boolean, note?: string) =>
  data<{ status: string }>(gatewayApi.post(`/api/v1/payroll/compensation-proposals/${id}/${confirm ? 'confirm' : 'reject'}`, { note }));

// --- HR Action Center (WorkforceAction; HR-gated performance:manage) ---
const HRA = '/api/v1/performance/hr/actions';
export interface WorkforceAction {
  id: string; employee_id: string; employee_name: string; signal_type: string; category: string;
  source_domain: string; severity: string; owner_type: string; status: string; priority: string;
  title: string; summary: string; due_at?: string | null; resolution_type?: string; resolution_note?: string;
  created_at: string;
}
export interface HRActionCategoryCount { category: string; open: number; overdue: number }
export const listHRActions = (category?: string, open = true) => {
  const qs = new URLSearchParams(); if (category) qs.set('category', category); qs.set('open', String(open));
  return data<{ actions: WorkforceAction[]; summary: HRActionCategoryCount[] }>(gatewayApi.get(`${HRA}?${qs}`));
};
export const getHRAction = (id: string) => data<{ action: WorkforceAction; events: any[] }>(gatewayApi.get(`${HRA}/${id}`));
export const acknowledgeHRAction = (id: string) => gatewayApi.post(`${HRA}/${id}/acknowledge`).then((r) => r.data);
export const setHRActionStatus = (id: string, status: string) => gatewayApi.post(`${HRA}/${id}/status`, { status }).then((r) => r.data);
export const resolveHRAction = (id: string, resolution_type: string, note?: string) => gatewayApi.post(`${HRA}/${id}/resolve`, { resolution_type, note }).then((r) => r.data);

// --- Social music (provider-neutral; external references + deep links only) --
const MUS = '/api/v1/performance/music';
export interface MusicChallenge {
  id: string; title: string; description?: string; challenge_type: string; status: string;
  visibility?: string; start_at?: string | null; end_at?: string | null;
  max_submissions_per_user: number; votes_per_user: number; allow_self_vote?: boolean;
  winner_count?: number; submission_count?: number; participant_count?: number;
}
export interface MusicSubmission {
  id: string; challenge_id: string; submitter_user_id: string; provider: string; ref_kind: string;
  canonical_url: string; title?: string; artist?: string; artwork_url?: string; vote_count: number; status: string;
}
export interface MusicSettings {
  enabled: boolean; playlists_enabled: boolean; submissions_enabled: boolean; voting_enabled: boolean;
  challenges_enabled: boolean; dana_enabled: boolean; conversations_enabled: boolean; provider: string;
}
export const getMusicSettings = () => data<MusicSettings>(gatewayApi.get(`${MUS}/settings`));
export const updateMusicSettings = (b: Partial<MusicSettings>) => data<MusicSettings>(gatewayApi.put(`${MUS}/settings`, b));
export const listMusicChallenges = () => data<{ challenges: MusicChallenge[] }>(gatewayApi.get(`${MUS}/challenges`)).then((r) => r.challenges || []);
export const getMusicChallenge = (id: string) => data<MusicChallenge>(gatewayApi.get(`${MUS}/challenges/${id}`));
export const createMusicChallenge = (b: Partial<MusicChallenge>) => data<MusicChallenge>(gatewayApi.post(`${MUS}/challenges`, b));
export const updateMusicChallenge = (id: string, b: Partial<MusicChallenge>) => data<MusicChallenge>(gatewayApi.patch(`${MUS}/challenges/${id}`, b));
export const openMusicChallenge = (id: string) => data<MusicChallenge>(gatewayApi.post(`${MUS}/challenges/${id}/open`));
export const closeMusicChallenge = (id: string) => data<MusicChallenge>(gatewayApi.post(`${MUS}/challenges/${id}/close`));
export const announceMusicChallenge = (id: string) => data<{ challenge: MusicChallenge; results: any[] }>(gatewayApi.post(`${MUS}/challenges/${id}/announce`));
export const listMusicSubmissions = (id: string) => data<{ submissions: MusicSubmission[] }>(gatewayApi.get(`${MUS}/challenges/${id}/submissions`)).then((r) => r.submissions || []);
export const submitMusicTrack = (id: string, b: { url: string; title?: string; artist?: string; note?: string }) =>
  data<MusicSubmission>(gatewayApi.post(`${MUS}/challenges/${id}/submissions`, b));
export const voteMusic = (submissionId: string) => data<{ voted: boolean }>(gatewayApi.post(`${MUS}/submissions/${submissionId}/vote`));
export const myMusicVotes = (challengeId: string) => data<{ submission_ids: string[] }>(gatewayApi.get(`${MUS}/challenges/${challengeId}/my-votes`)).then((r) => r.submission_ids || []);
export const musicTrending = () => data<{ trending: MusicSubmission[] }>(gatewayApi.get(`${MUS}/trending`)).then((r) => r.trending || []);
export interface CompanyMixTrack { provider: string; canonical_url: string; title: string; artist: string; votes: number }
export interface CompanyMix { title: string; subtitle: string; track_count: number; tracks: CompanyMixTrack[] }
export const getCompanyMix = () => data<CompanyMix>(gatewayApi.get(`${MUS}/company-mix`));

// Playlists (incl. CIRCLE-scoped circle playlists). Create/add require recognition:manage.
export interface MusicPlaylist { id: string; name: string; description?: string; scope_type: string; scope_id?: string; provider: string; item_count?: number; created_at: string }
// external_id + ref_kind are what the player queues from — the service already parsed the
// pasted link, so the UI never has to re-derive an id from the URL (see lib/playableRef.ts).
export interface MusicPlaylistItem { id: string; provider: string; external_id?: string; canonical_url: string; title?: string; artist?: string; artwork_url?: string; ref_kind?: string }
export const listMusicPlaylists = (scope?: string, scopeId?: string) => {
  const qs = new URLSearchParams(); if (scope) qs.set('scope', scope); if (scopeId) qs.set('scope_id', scopeId);
  return data<{ playlists: MusicPlaylist[] }>(gatewayApi.get(`${MUS}/playlists${qs.toString() ? `?${qs}` : ''}`)).then((r) => r.playlists || []);
};
export const createMusicPlaylist = (b: { name: string; description?: string; scope_type: string; scope_id?: string }) => data<MusicPlaylist>(gatewayApi.post(`${MUS}/playlists`, b));
export const getMusicPlaylist = (id: string) => data<{ playlist: MusicPlaylist; items: MusicPlaylistItem[] }>(gatewayApi.get(`${MUS}/playlists/${id}`));
export const addMusicPlaylistItem = (id: string, b: { url: string; title?: string; artist?: string; kind?: string }) => data<MusicPlaylistItem>(gatewayApi.post(`${MUS}/playlists/${id}/items`, b));
// Personal playlists ("My Playlists") — any employee, owner-scoped.
export const listMyPlaylists = () => data<{ playlists: MusicPlaylist[] }>(gatewayApi.get(`${MUS}/playlists/mine`)).then((r) => r.playlists || []);
export const createMyPlaylist = (b: { name: string; description?: string }) => data<MusicPlaylist>(gatewayApi.post(`${MUS}/playlists/mine`, b));
export const getMyPlaylist = (id: string) => data<{ playlist: MusicPlaylist; items: MusicPlaylistItem[] }>(gatewayApi.get(`${MUS}/playlists/mine/${id}`));
export const addMyPlaylistItem = (id: string, b: { url: string; title?: string; artist?: string; kind?: string }) => data<MusicPlaylistItem>(gatewayApi.post(`${MUS}/playlists/mine/${id}/items`, b));
export const shareMusicChallenge = (id: string) => data<{ shared: boolean }>(gatewayApi.post(`${MUS}/challenges/${id}/share`));

// --- Apple Music PERSONAL account connection (fail-closed) ---
// The developer token is minted server-side (ES256) and returned only for MusicKit-JS
// to initialise in the browser; the .p8 private key never leaves the server. The Music
// User Token from authorize() stays client-side in MusicKit — never sent here; /connect
// records only that the employee authorised. `enabled` from /apple/status is the
// capability-discovery signal (flag AND creds AND tenant): when false, the client makes
// no token/connect requests. Personal + catalog only — no sponsored seats/billing.
export interface AppleMusicStatus { connected: boolean; enabled: boolean }
export interface AppleMusicToken { token: string; expires_at: string; media_id: string }
export const getAppleMusicStatus = () => data<AppleMusicStatus>(gatewayApi.get(`${MUS}/apple/status`));

// Lyrics come ONLY from a licensed backend adapter (never scraped/LLM). Fail-closed: the
// endpoint returns {status:'unavailable'|'restricted'|'instrumental'|'available'} — the
// client renders honest states and never fabricates lyrics. Raw lyrics are never logged.
export interface LyricsLineDTO { startMs?: number; endMs?: number; text: string }
export type LyricsResultDTO =
  | { status: 'available'; mode: 'synchronized' | 'plain'; language?: string; lines: LyricsLineDTO[]; attribution: { provider: string; text: string; url?: string }; providerTrackId: string }
  | { status: 'instrumental' | 'unavailable' | 'restricted'; attribution?: { provider: string; text: string; url?: string } };
export const getSongLyrics = (q: { isrc?: string; title: string; artist: string; album?: string; durationMs?: number; appleMusicCatalogId?: string; storefront?: string }) =>
  data<LyricsResultDTO>(gatewayApi.post(`${MUS}/lyrics`, q));
export const getAppleMusicDeveloperToken = () => data<AppleMusicToken>(gatewayApi.get(`${MUS}/apple/developer-token`));
export const connectAppleMusic = () => data<{ connected: boolean }>(gatewayApi.post(`${MUS}/apple/connect`));
export const disconnectAppleMusic = () => data<{ connected: boolean }>(gatewayApi.delete(`${MUS}/apple/connect`));

// --- Employee Surveys & Listening (Phase 1) ----------------------------------
// Admin talks to /performance/surveys (HR-gated); participants to /performance/my/surveys.
// Anonymity is enforced server-side: results below the min-N floor come back {revealed:false}.
const SVY = `${BASE}/surveys`;
const MYS = `${BASE}/my/surveys`;

export type SurveyIdentityMode = 'NAMED' | 'ANONYMOUS' | 'CONFIDENTIAL';
export type SurveyStatus = 'Draft' | 'Scheduled' | 'Open' | 'Paused' | 'Closed' | 'Archived';
export type SurveyQuestionType = 'single' | 'multi' | 'yesno' | 'rating' | 'numeric' | 'short_text' | 'long_text' | 'nps';

export interface SurveyQuestion {
  id?: string; position?: number; type: SurveyQuestionType; prompt: string;
  required?: boolean; config?: Record<string, unknown>;
}
export interface Survey {
  id: string; title: string; description: string; identity_mode: SurveyIdentityMode;
  min_anonymous_responses: number; status: SurveyStatus; required: boolean;
  opens_at?: string | null; closes_at?: string | null; timezone: string;
  audience_rule?: unknown; published_at?: string | null; closed_at?: string | null;
  created_at: string; updated_at: string; questions?: SurveyQuestion[];
  invited_count: number; responded_count: number;
}
export interface SurveyInput {
  title: string; description?: string; identity_mode?: SurveyIdentityMode;
  min_anonymous_responses?: number; required?: boolean;
  opens_at?: string | null; closes_at?: string | null; timezone?: string;
  audience_rule?: Record<string, unknown>;
}
export interface SurveyQuestionResult {
  question_id: string; type: SurveyQuestionType; prompt: string; answered: number;
  counts?: Record<string, number>; average?: number; comments?: string[];
}
export interface SurveyResults {
  survey_id: string; identity_mode: SurveyIdentityMode; invited_count: number;
  responded_count: number; response_rate: number; min_n: number; revealed: boolean;
  /** How many questions the survey has; set even while results are hidden. */
  question_count?: number;
  questions?: SurveyQuestionResult[];
}
export interface MySurvey {
  id: string; title: string; description: string; anonymous: boolean;
  identity_mode: SurveyIdentityMode; closes_at?: string | null; responded: boolean; required: boolean;
}

// Admin (HR)
export const listSurveys = (status?: SurveyStatus) =>
  data<{ surveys: Survey[] }>(gatewayApi.get(SVY, { params: status ? { status } : {} })).then((r) => r.surveys || []);
export const getSurvey = (id: string) => data<{ survey: Survey }>(gatewayApi.get(`${SVY}/${id}`)).then((r) => r.survey);
export const createSurvey = (b: SurveyInput) => data<{ survey: Survey }>(gatewayApi.post(SVY, b)).then((r) => r.survey);
export const updateSurvey = (id: string, b: SurveyInput) => data<{ survey: Survey }>(gatewayApi.patch(`${SVY}/${id}`, b)).then((r) => r.survey);
export const setSurveyQuestions = (id: string, questions: SurveyQuestion[]) => gatewayApi.put(`${SVY}/${id}/questions`, { questions }).then((r) => r.data);
export const publishSurvey = (id: string) => data<{ survey: Survey; invited: number }>(gatewayApi.post(`${SVY}/${id}/publish`, {}));
export const setSurveyStatus = (id: string, status: SurveyStatus) => gatewayApi.post(`${SVY}/${id}/status`, { status }).then((r) => r.data);
export const remindSurvey = (id: string) => data<{ reminded: number }>(gatewayApi.post(`${SVY}/${id}/remind`, {}));
export const getSurveyResults = (id: string) => data<{ results: SurveyResults }>(gatewayApi.get(`${SVY}/${id}/results`)).then((r) => r.results);
export const createSurveyFollowup = (id: string, title?: string, note?: string) => gatewayApi.post(`${SVY}/${id}/followup`, { title, note }).then((r) => r.data);

// Participant
export const listMySurveys = () => data<{ surveys: MySurvey[] }>(gatewayApi.get(MYS)).then((r) => r.surveys || []);
export const getSurveyToTake = (id: string) => data<{ survey: Survey }>(gatewayApi.get(`${MYS}/${id}`)).then((r) => r.survey);
export const submitSurvey = (id: string, answers: { question_id: string; value: unknown; comment?: string }[]) =>
  gatewayApi.post(`${MYS}/${id}/respond`, { answers }).then((r) => r.data);

// --- HR Action Center config (Phase 3/4 activation) --------------------------
const HRCFG = `${BASE}/hr/config`;
export interface HRActionConfig {
  review_followup_rating_max?: number | null;
  onboarding_checkin_enabled?: boolean;
  onboarding_checkin_days?: number;
}
export const getHRConfig = () => data<{ config: HRActionConfig }>(gatewayApi.get(HRCFG)).then((r) => r.config);
export const saveHRConfig = (cfg: HRActionConfig) => data<{ config: HRActionConfig }>(gatewayApi.put(HRCFG, cfg)).then((r) => r.config);
